import axios from "axios"
import { Component } from "react"
import {
	FormControl,
	OverlayTrigger,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from "react-bootstrap"
import Loader from "../../components/common/Loader"
import AxiosConfig from "../../utilities/AxiosConfig"
import snackBar from "../../utilities/snackBar/SnackBar"
import NewVehicle from "./NewVehicle/NewVehicle"
import VehicleAdvancedSearch from "./VehicleAdvancedSearch/VehicleAdvancedSearch"
import VehicleFilterSearch from "./VehicleFilterSearch/VehicleFilterSearch"
import VehicleTable from "./VehicleTable/VehicleTable"
import "./Fleet.css"
import type { I_VehicleLeaseGridView } from "src/common_interfaces/I_VehicleLease"
import { shouldAllowAccess } from "src/utilities/helper"
import LeaseTable from "./LeaseTable/LeaseTable"

type FleetTypes = {
	loadingVehicles: boolean
	vehicles: Array<any>
	vehiclesCopy: Array<any>
	typingTimeout: number | any
	input: string
	typing: boolean
	filterSearchPlaceholder: string
	cardDex: number
	advancedSearch: boolean
	fleetReference: any
	searchParams: object
	newVehicle: boolean
	viewMode: "vehicles" | "lease"
	leases: Array<I_VehicleLeaseGridView>
	loadingLeases: boolean
	leasesError: string | null
	leasesLoaded: boolean
	leaseFilterInput: string
	leaseStatusFilter: string
}

class Fleet extends Component<{}, FleetTypes> {
	constructor(props: {}) {
		super(props)
		this.state = {
			loadingVehicles: false,
			vehicles: [],
			vehiclesCopy: [],
			typing: false,
			typingTimeout: 0,
			input: "",
			filterSearchPlaceholder: "Search",
			cardDex: 0,
			advancedSearch: false,
			searchParams: {},
			fleetReference: {},
			newVehicle: false,
			viewMode: "vehicles",
			leases: [],
			loadingLeases: false,
			leasesError: null,
			leasesLoaded: false,
			leaseFilterInput: "",
			leaseStatusFilter: "All",
		}
		this.clearInput = this.clearInput.bind(this)
		this.toggleAdvancedSearch = this.toggleAdvancedSearch.bind(this)
	}

	componentDidMount() {
		this.getFleetReference()
		const location = window.location.href.split("&")
		const param: any = {}
		if (location.length > 1) {
			for (let i = 1; i < location.length; i++) {
				const temp = location[i].split("=")
				param[temp[0]] = temp[1]
			}
			this.getVehicles(param)
		} else {
			this.getVehicles({ VehicleStatusTypeId: "1" })
		}
	}

	getVehicles = (input: object) => {
		this.toggleAdvancedSearch("off")
		this.setState({ loadingVehicles: true, vehicles: [] })
		const requestBody = input
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/Fleet/Search`,
				requestBody,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({
					vehicles: res.data,
					vehiclesCopy: res.data,
					loadingVehicles: false,
					searchParams: input,
				})
				if (res.data.length === 0) {
					snackBar({ text: "Nothing Found", type: "error", timeout: 3000 })
				}
			})
			.catch((err) => {
				//console.log(err, err.response);
				AxiosConfig.handleError(err)
			})
	}

	getLeases = () => {
		this.setState({ loadingLeases: true, leasesError: null })
		axios
			.get<Array<I_VehicleLeaseGridView>>(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/all`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({
					leases: res.data,
					loadingLeases: false,
					leasesLoaded: true,
				})
			})
			.catch((err) => {
				this.setState({
					loadingLeases: false,
					leasesError: "Lease information could not be loaded.",
				})
				AxiosConfig.handleError(err)
			})
	}

	// Fetches on first switch to the lease view only — keeps the request off the
	// initial page load and off users without the lease role.
	setViewMode = (viewMode: "vehicles" | "lease") => {
		this.setState({ viewMode })
		if (viewMode === "lease" && !this.state.leasesLoaded) {
			this.getLeases()
		}
	}

	handleLeaseSearchChange = (input: string) => {
		this.setState({
			leaseFilterInput: input
		})
	}

	handleLeaseStatusChange = (status: string) => {
		this.setState({ leaseStatusFilter: status })
	}

	getFleetReference = () => {
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/Fleet/reference`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({ fleetReference: res.data })
			})
			.catch((_err) => {
				//console.log(err)
			})
	}

	handleFilter = (event: { target: { value: any } }) => {
		this.setState({ input: event.target.value })
		this.closeDoublePanel()
		const self = this
		const input = event.target.value
		if (self.state.typingTimeout) {
			clearTimeout(self.state.typingTimeout)
		}
		self.setState({
			typing: false,
			typingTimeout: setTimeout(() => {
				if (self.state.input.length > 0) {
					self.filter(input, self.state.vehiclesCopy)
				} else {
					self.setState({ vehicles: self.state.vehiclesCopy })
				}
			}, 500),
		})
	}

	filter = (input: string, vehicles: Array<object>) => {
		const temp = []
		for (let i = 0; i < vehicles.length; i++) {
			const str = Object.values(vehicles[i]).join().toLowerCase()
			if (str.includes(input.toLowerCase())) {
				temp.push(vehicles[i])
			}
		}
		if (temp !== this.state.vehicles) {
			this.setState({ vehicles: temp })
		}
	}

	clearInput = () => {
		this.setState({ input: "", vehicles: this.state.vehiclesCopy })
	}

	closeDoublePanel = () => {
		if (document.getElementById("fleet-overview-close-btn")) {
			const closePanel: any = document.getElementById(
				"fleet-overview-close-btn",
			)
			closePanel.click()
		}
	}

	toggleAdvancedSearch = (args: string) => {
		this.closeDoublePanel()
		if (args === "off") {
			this.setState({ advancedSearch: false })
		} else {
			if (this.state.advancedSearch) {
				this.setState({ advancedSearch: false })
			} else {
				this.setState({ advancedSearch: true })
			}
		}
	}

	toggleNewVehicle = () => {
		if (this.state.newVehicle) {
			this.setState({ newVehicle: false })
		} else {
			this.setState({ newVehicle: true })
		}
	}

	render() {
		const addNewVehicleTooltip = (
			<Tooltip id="vi-pin-tooltip">Add a new vehicle</Tooltip>
		)

		// //console.log(this.state.loadingVehicles)

		return (
			<div className="Fleet main-content col-sm">
				<div className="VehicleSearchInputWrapper">
					{this.state.viewMode === "vehicles" && <div className="VehicleFilterSearchWrapper">
						<VehicleFilterSearch
							input={this.state.input}
							placeholder={this.state.filterSearchPlaceholder}
							handleFilter={this.handleFilter}
							clearForm={this.clearInput}
							toggleAdvancedSearch={this.toggleAdvancedSearch}
						/>
					</div>}
					{this.state.viewMode === "vehicles" && this.state.fleetReference.vehicleMakes && (
						<div className="AdvancedSearchWrapper">
							<VehicleAdvancedSearch
								advancedSearch={this.state.advancedSearch}
								toggleAdvancedSearch={this.toggleAdvancedSearch}
								getVehicles={this.getVehicles}
								fleetReference={this.state.fleetReference}
							/>
						</div>
					)}

					{this.state.viewMode === "lease" && (
						<div className="LeaseFilterWrapper">
							<FormControl
								value={this.state.leaseFilterInput}
								placeholder="Search by unit number"
								onChange={(e) => this.handleLeaseSearchChange(e.target.value)}
							/>
							<FormControl
								as="select"
								aria-label="Filter leases by status"
								value={this.state.leaseStatusFilter}
								onChange={(e) => this.handleLeaseStatusChange(e.target.value)}
							>
								<option value="All">All statuses</option>
								<option value="Active">Active</option>
								<option value="Expired">Expired</option>
								<option value="Terminated">Terminated</option>
							</FormControl>
						</div>
					)}

					{shouldAllowAccess("FLEET:LEASEUSER") &&
					!this.state.loadingVehicles ? (
						<div className="LeaseViewToggleWrapper">
							<ToggleButtonGroup
								type="radio"
								name="fleet-view-mode"
								value={this.state.viewMode}
								onChange={this.setViewMode}
							>
								<ToggleButton value="vehicles" variant="outline-secondary">
									Vehicles
								</ToggleButton>
								<ToggleButton value="lease" variant="outline-secondary">
									Leases
								</ToggleButton>
							</ToggleButtonGroup>
						</div>
					) : null}
				</div>
				{this.state.viewMode === "vehicles" && !this.state.loadingVehicles && (
					<OverlayTrigger
						trigger={["hover", "focus"]}
						placement="auto-end"
						delay={0}
						overlay={addNewVehicleTooltip}
					>
						<span
							className="material-icons"
							id="toggle-new-vehicle-view-btn"
							onClick={this.toggleNewVehicle}
						>
							add
						</span>
					</OverlayTrigger>
				)}
				<div className="VehicleTableWrapper">
					{/* Hidden rather than unmounted — DoublePanel holds the rows in its own
					    state and does not repopulate reliably on remount. */}
					<div
						style={{
							display: this.state.viewMode === "vehicles" ? undefined : "none",
						}}
					>
						<VehicleTable
							vehicles={this.state.vehicles}
							input={this.state.input}
							fleetReference={this.state.fleetReference}
						/>
					</div>
					{this.state.viewMode === "lease" ? (
						<LeaseTable
							leases={this.state.leases}
							loading={this.state.loadingLeases}
							error={this.state.leasesError}
							searchString={this.state.leaseFilterInput}
							statusFilter={this.state.leaseStatusFilter}
						/>
					) : null}
				</div>
				{this.state.loadingVehicles && (
					<div className="LoadingVehicles">
						<Loader title="Loading Vehicles" />
					</div>
				)}
				{this.state.newVehicle && (
					<NewVehicle
						getVehicles={this.getVehicles}
						fleetReference={this.state.fleetReference}
						toggleNewVehicle={this.toggleNewVehicle}
					/>
				)}
			</div>
		)
	}
}

export default Fleet
